import type * as THREE from 'three';
import { IMPACT } from '../config/tuning';
import type { TargetParams, WarheadParams } from '../config/targets';

/**
 * Graded impact resolution (SPEC §3.8.2).
 *
 * ```
 * effect = warheadYield · f(impactSpeed) · g(diveAngle, targetHardness) · aimPointDistanceFalloff
 * ```
 *
 * The grading is the design point, not an implementation detail. SPEC §3.8.2 is explicit that
 * outcomes are "graded, not binary… graded outcomes make near-successes feel like progress rather
 * than failure" — so a run that arrives 12 m off the aim point returns a NEAR MISS with a real
 * number attached, not a silent nothing. A player who can see they were close will fly it again;
 * a player who gets a boolean will not.
 *
 * Everything here is pure. Units: metres, m/s, radians in, degrees only at the config boundary.
 */

export const ImpactGrade = {
  /** Outside the target's body entirely, or the warhead failed to function. */
  Miss: 'MISS',
  /** Struck, but the effect was not enough to satisfy the objective. Target degraded. */
  NearMiss: 'NEAR MISS',
  /** Objective satisfied. */
  Hit: 'HIT',
  /** Aim point struck within `directHitRadiusM`: full destruction, bonus score. */
  DirectHit: 'DIRECT HIT',
} as const;
export type ImpactGradeId = (typeof ImpactGrade)[keyof typeof ImpactGrade];

/** Why an attack failed, so the debrief can say something more useful than "miss". */
export const ImpactFailure = {
  None: 'none',
  OutsideBody: 'outside-body',
  TooSlow: 'too-slow',
  TooShallow: 'too-shallow',
  WrongWarhead: 'wrong-warhead',
  /** Struck the target, but beyond the warhead's effect radius — a hit with nothing behind it. */
  OutsideEffect: 'outside-effect',
} as const;
export type ImpactFailureId = (typeof ImpactFailure)[keyof typeof ImpactFailure];

export interface ImpactResult {
  grade: ImpactGradeId;
  failure: ImpactFailureId;
  /** Fraction of the target's integrity removed, 0–1. */
  effect: number;
  /** Distance from the aim point, metres. */
  aimPointDistanceM: number;
  impactSpeedMs: number;
  /** Dive angle at impact, degrees below horizontal. */
  diveAngleDeg: number;
}

export function createImpactResult(): ImpactResult {
  return {
    grade: ImpactGrade.Miss,
    failure: ImpactFailure.None,
    effect: 0,
    aimPointDistanceM: 0,
    impactSpeedMs: 0,
    diveAngleDeg: 0,
  };
}

/**
 * Speed term `f(impactSpeed)`, unitless.
 *
 * Below the target's minimum the warhead does not function at all — a slow arrival bounces. Above
 * it the effect grows with speed and saturates, because past a point the warhead is the limit
 * rather than the kinetic energy.
 */
export function speedFactor(impactSpeedMs: number, minImpactSpeedMs: number): number {
  if (impactSpeedMs < minImpactSpeedMs) return 0;
  const reference = Math.max(IMPACT.minEffectiveSpeedMs, minImpactSpeedMs);
  const ratio = impactSpeedMs / Math.max(reference, 1);
  return Math.min(1, 0.45 + 0.55 * Math.min(ratio, 2) * 0.5);
}

/**
 * Dive and hardness term `g(diveAngle, targetHardness)`, unitless.
 *
 * A hardened target only yields to a warhead that can reach through it, and — for the bunker —
 * only to one arriving steeply enough to strike the roof rather than the berm. A soft target does
 * not care from which direction it is hit.
 */
export function diveHardnessFactor(
  diveAngleDeg: number,
  target: TargetParams,
  warhead: WarheadParams,
): number {
  // Softness the warhead has to overcome. A frag warhead against a bunker is mostly wasted.
  const resistance = target.hardness * (1 - warhead.penetration);
  const base = 1 - resistance;

  if (target.minDiveAngleDeg <= 0) return base;

  // Past the gate the angle stops helping; short of it the attack has already failed elsewhere.
  const steepness = Math.min(1, diveAngleDeg / target.minDiveAngleDeg);
  return base * (0.4 + 0.6 * steepness);
}

/**
 * Falloff with distance from the aim point, unitless.
 *
 * Uses the warhead's own effect radius rather than the target's size: a fragmentation warhead
 * spreads its effect wide and forgives an imprecise run, a penetrator concentrates it and does
 * not. That trade is the whole reason both exist.
 */
export function aimPointFalloff(distanceM: number, warhead: WarheadParams): number {
  if (distanceM <= 0) return 1;
  const normalised = distanceM / warhead.effectRadiusM;
  if (normalised >= 1) return 0;
  return (1 - normalised) ** IMPACT.falloffExp;
}

/** Dive angle of a velocity vector, degrees below horizontal. Negative means climbing. */
export function diveAngleDeg(velocityMs: THREE.Vector3): number {
  const speed = velocityMs.length();
  if (speed < 1e-6) return 0;
  return (Math.asin(-velocityMs.y / speed) * 180) / Math.PI;
}

/**
 * Resolve one impact against one target.
 *
 * `aimPointDistanceM` is measured from the target's aim point, not its centre — the two differ
 * for anything with a roof or a waterline, and hitting the wrong part of a correct target is a
 * different outcome from hitting nothing.
 */
export function resolveImpact(
  aimPointDistanceM: number,
  impactSpeedMs: number,
  diveDeg: number,
  target: TargetParams,
  warhead: WarheadParams,
  out: ImpactResult,
): ImpactResult {
  out.aimPointDistanceM = aimPointDistanceM;
  out.impactSpeedMs = impactSpeedMs;
  out.diveAngleDeg = diveDeg;
  out.effect = 0;
  out.grade = ImpactGrade.Miss;
  out.failure = ImpactFailure.None;

  if (aimPointDistanceM > target.bodyRadiusM) {
    out.failure = ImpactFailure.OutsideBody;
    return out;
  }
  if (impactSpeedMs < target.minImpactSpeedMs) {
    out.failure = ImpactFailure.TooSlow;
    return out;
  }
  if (target.requiresPenetrator && warhead.penetration < 1) {
    // Struck, and did something — but not what this target needs.
    out.failure = ImpactFailure.WrongWarhead;
    out.grade = ImpactGrade.NearMiss;
    out.effect = 0.2 * aimPointFalloff(aimPointDistanceM, warhead);
    return out;
  }
  if (target.minDiveAngleDeg > 0 && diveDeg < target.minDiveAngleDeg) {
    out.failure = ImpactFailure.TooShallow;
    out.grade = ImpactGrade.NearMiss;
    out.effect = 0.25 * aimPointFalloff(aimPointDistanceM, warhead);
    return out;
  }

  out.effect =
    warhead.yield *
    speedFactor(impactSpeedMs, target.minImpactSpeedMs) *
    diveHardnessFactor(diveDeg, target, warhead) *
    aimPointFalloff(aimPointDistanceM, warhead);

  if (aimPointDistanceM <= target.directHitRadiusM && out.effect >= IMPACT.hitEffect) {
    out.grade = ImpactGrade.DirectHit;
    // A direct hit destroys outright — the aim point is the aim point.
    out.effect = 1;
  } else if (out.effect >= IMPACT.hitEffect) {
    out.grade = ImpactGrade.Hit;
  } else if (out.effect > 0) {
    out.grade = ImpactGrade.NearMiss;
  } else {
    // Inside the body but outside what this warhead can reach: a genuine strike that achieved
    // nothing, which is a different lesson from missing and deserves a different word for it.
    out.grade = ImpactGrade.Miss;
    out.failure = ImpactFailure.OutsideEffect;
  }

  return out;
}

/** True when the grade satisfies a mission objective (SPEC §3.8.2). */
export function satisfiesObjective(grade: ImpactGradeId): boolean {
  return grade === ImpactGrade.Hit || grade === ImpactGrade.DirectHit;
}
